import React, { useRef, useEffect, useState, useContext } from 'react';
import ARCMap from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import Basemap from '@arcgis/core/Basemap.js';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import Extent from '@arcgis/core/geometry/Extent.js';
import SpatialReference from '@arcgis/core/geometry/SpatialReference.js';
import { InterfaceContext } from './store/InterfaceProvider';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils.js';
import { generateChloroplethStyleJson, generateInfoStyleJson, generateHoverStyleJson } from './utils/generateVectorTileStyleJson';
import Graphic from '@arcgis/core/Graphic.js';
import { DATA_URL, getScaleObject, symbology } from './store/StaticVariables';
import WebStyleSymbol from '@arcgis/core/symbols/WebStyleSymbol.js';
import AlaskaWidget from './components/AlaskaWidget';
import HawaiiWidget from './components/HawaiiWidget';
import RemoveMarkerWidget from './components/RemoveMarkerWidget';
import FullExtentWidget from './components/FullExtentWidget';
import { geometryScaleFromGeoid } from './utils/DataTransformation';

const Map = () => {
    const { searchResult, setSearchResult, activeViewType, activeScale, activeTimePeriod, activeDeathType, activeMeasurement, mapReadOnlyZoom, selectedFeature, setSelectedFeature, setMapReadOnlyZoom, setHoveredFeature, activeMapAttribute } = useContext(InterfaceContext);

    const mapDiv = useRef(null);
    const [initMap, setInitMap] = useState(false);
    const view = useRef(null);
    const map = useRef(null);
    const chloroplethLayer = useRef(null);
    const chloroplethLoading = useRef(false);
    const infoLayer = useRef(null);
    const hoverLayer = useRef(null);
    const zoomRef = useRef(mapReadOnlyZoom);
    const dataRef = useRef({
        scale: activeScale,
        view: activeViewType,
        timePeriod: activeTimePeriod,
        deathType: activeDeathType,
        measurment: activeMeasurement,
        mapAttribute: activeMapAttribute
    });
    const selectedFeatureGEOID = useRef(0);
    const hoveredFeatureGEOID = useRef(0);

    const getGeoidFromInteration = (response) => {
        const attr = getAttributesFromInteraction(response);
        if ('geoid' in attr) return attr.geoid;
        if (!attr?.GEOID) return 0;
        return attr.GEOID;
    };

    const filterGraphicsByStyleLayer = (results, styleLayerFilter = '') => {
        return results.filter((result) => {
            return result.graphic.origin.layerId.toLowerCase().indexOf(styleLayerFilter.toLowerCase()) === 0;
        });
    };
    const filterGraphicsByScale = (results) => {
        return results.filter((result) => {
            var geoid = result.graphic.attributes?.GEOID || result.graphic.attributes?.geoid || false;
            const geoScale = geometryScaleFromGeoid(geoid);
            return geoid && geoScale === dataRef.current.scale;
        });
    };
    const resultWithinHitLayer = (result) => {
        return result?.layer?.id === 'hit-layer';
    };
    const resultWithinScale = (result) => {
        var geoid = result.graphic.attributes?.GEOID || result.graphic.attributes?.geoid || false;
        const geoScale = geometryScaleFromGeoid(geoid);
        return geoid && geoScale === dataRef.current.scale;
    };
    const filterGraphicsByHitLayer = (results) => {
        return results.filter((result) => {
            return resultWithinHitLayer(result) && resultWithinScale(result);
        });
    };
    const filterGraphicsByAttribute = (results) => {
        return results.filter((result) => {
            // console.log(result);
            // console.log('ATTR', dataRef.current.mapAttribute);
            // console.log(result.graphic.attributes);
            return dataRef.current.mapAttribute in result.graphic.attributes;
        });
    };

    const getAttributesFromInteraction = (response) => {
        if (!response?.results?.length) return {};
        // const validResults = filterGraphicsByScale(response.results);
        const validResults = filterGraphicsByHitLayer(response.results);
        if (!validResults.length) return {};
        const validResults2 = filterGraphicsByAttribute(validResults);
        if (!validResults2.length) return {};
        return validResults2[0].graphic.attributes;
    };
    // useEffect(() => {
    //     console.log('hv', hoveredGeoid);
    // }, [hoveredGeoid]);
    const handleFeatureHover = async (event) => {
        if (view.current.zoom < getScaleObject(dataRef.current.scale)?.minZoom) return;
        if (event.pointerType  ==='touch') {return;} 

        view.current.hitTest(event).then((res) => {
            // const attributes = getAttributesFromInteraction(res);
            // console.log(attributes);
            // setHoveredFeature(attributes);
            const geoid = getGeoidFromInteration(res);
            // console.log(getAttributesFromInteraction(res));
            if (geoid !== hoveredFeatureGEOID.current) {
                // console.log('hh Attr', geoid, hoveredGeoid);
                // styleHoveredFeature(geoid);
                hoveredFeatureGEOID.current = geoid;
                // setHoveredGeoid(geoid);
                const attributes = getAttributesFromInteraction(res);
                // console.log(attributes);
                setHoveredFeature(attributes);
            }
        });
    };

    const scaleSymbol = (symbol) => {
        return symbol.fetchCIMSymbol().then(function (cimSymbol) {
            cimSymbol.data.symbol.symbolLayers.forEach(function (symbolLayer) {
                symbolLayer.size = symbolLayer.size * 2;
            });
            return cimSymbol;
        });
    };
    const addPinToMap = async (x, y, wkid = 4326) => {
        const pin = new WebStyleSymbol({
            name: 'tear-pin-2',
            styleName: 'Esri2DPointSymbolsStyle'
        });
        const pinScaled = await scaleSymbol(pin);
        const graphic = new Graphic({
            geometry: {
                type: 'point',
                x: x,
                y: y,
                spatialReference: new SpatialReference({ wkid })
            },
            symbol: pinScaled
        });
        view.current.graphics.removeAll();
        view.current.graphics.add(graphic);
    };
    // useEffect(() => {
    //     styleHoveredFeature(hoveredGeoid);
    // }, [hoveredGeoid]);

    const handleFeatureClick = (event) => {
        view.current.hitTest(event).then((res) => {
            const feature = getAttributesFromInteraction(res);
            // const geoid = feature?.GEOID ? feature.GEOID : 0;
            setSelectedFeature(feature);
            // console.log(feature);
            // styleSelectedFeature(geoid);
        });
    };

    const styleHoveredFeature = (geoid) => {
        // console.log('h Attr', geoid);
        // let newGeoid = selectedFeatureGEOID.current === geoid ? '1' : geoid;
        // updatehoveredStyleLayer('hovered_feature', (lyr) => {
        //     lyr.filter[2] = newGeoid;
        //     return lyr;
        // });
    };

    const styleSelectedFeature = (geoid) => {
        const featureScale = geometryScaleFromGeoid(geoid);
        const sourceLayer = getScaleObject(featureScale) ? getScaleObject(featureScale)['source-layer'] : 'State';
        updateSelectedStyleLayer('selected_feature', (lyr) => {
            lyr.filter[2] = geoid;
            lyr['source-layer'] = sourceLayer;
            return lyr;
        });
    };

    const moveMapToExtent = (extent, wkid = 4326) => {
        const { xmin, ymin, xmax, ymax } = extent;
        const newExtent = new Extent({ xmin, ymin, xmax, ymax, spatialReference: new SpatialReference({ wkid }) });
        view.current.goTo(newExtent);
    };

    const showRelevantScaleStyleLayers = () => {
        if (!chloroplethLayer.current || !'getStyleLayer' in chloroplethLayer.current) return;

        if (dataRef.current.view === 'deaths') {
            //show census hide zcta
            chloroplethLayer.current.setStyleLayerVisibility('ZCTA', 'none');
            chloroplethLayer.current.setStyleLayerVisibility('Census', 'visible');
        } else {
            //hide zcta show census
            chloroplethLayer.current.setStyleLayerVisibility('ZCTA', 'visible');
            chloroplethLayer.current.setStyleLayerVisibility('Census', 'none');
        }
    };

    const reLoadChloropleth = () => {
        // console.log('==> generate style', activeTimePeriod, dataRef.current.timePeriod);
        if (!chloroplethLayer.current) return;
        chloroplethLoading.current = true;
        const newJson = generateChloroplethStyleJson({
            timePeriod: activeTimePeriod,
            sourceLayer: getScaleObject(dataRef.current.scale)?.['source-layer'],
            view: activeViewType,
            mapAttribute: activeMapAttribute,
            scale: dataRef.current.scale
        });
        chloroplethLayer.current.loadStyle(newJson).then(() => {
            // console.log('layer has loaded');
            chloroplethLoading.current = false;
        });
    };

    const reColorStyleStepLayers = () => {
        // console.log('re-color step layers');
        const colors = symbology[activeScale][activeMapAttribute];
        colors.forEach((step, i) => {
            updateChloroplethStyleLayer(step.name, (lyr) => {
                lyr.filter[1] = activeMapAttribute;
                lyr.paint['fill-color'] = step.color;
                return lyr;
            });
        });
        updateChloroplethStyleLayer('Lines', (lyr) => {
            lyr.filter[1] = activeMapAttribute;
            return lyr;
        });
        updateChloroplethStyleLayer('Lines_zero', (lyr) => {
            lyr.filter[1] = activeMapAttribute;
            return lyr;
        });
        updateChloroplethStyleLayer('unstable_pattern', (lyr) => {
            lyr.filter[1] = activeMapAttribute;
            return lyr;
        });
    };
    const rescaleStyleStepLayers = () => {
        // console.log('re-cale step layers');
        const colors = symbology[activeScale][activeMapAttribute];
        const scaleObj = getScaleObject(dataRef.current.scale);
        const sourceLayer = scaleObj?.['source-layer'];
        const minZoom = scaleObj.minZoom;
        colors.forEach((step) => {
            updateChloroplethStyleLayer(step.name, (lyr) => {
                lyr['source-layer'] = sourceLayer;
                lyr.minzoom = minZoom;
                return lyr;
            });
        });
        updateChloroplethStyleLayer('Lines', (lyr) => {
            lyr['source-layer'] = sourceLayer;
            return lyr;
        });
        updateChloroplethStyleLayer('Lines_zero', (lyr) => {
            lyr['source-layer'] = sourceLayer;
            return lyr;
        });
        updateChloroplethStyleLayer('unstable_pattern', (lyr) => {
            lyr['source-layer'] = sourceLayer;
            return lyr;
        });
    };
    const rescaleSelectedFeature = () => {
        const sourceLayer = getScaleObject(dataRef.current.scale)?.['source-layer'];
        updateSelectedStyleLayer('selected_feature', (lyr) => {
            lyr['source-layer'] = sourceLayer;
            return lyr;
        });
    };

    const clearHoveredFeature = (evt) => {
        styleHoveredFeature('1');
        setHoveredFeature({});
        setTimeout(() => styleHoveredFeature('1'), 100);
    };
    const resetInteractiveFeatures = () => {
        selectedFeatureGEOID.current = 1;
        updatehoveredStyleLayer('selected_feature', (lyr) => {
            lyr.filter[2] = '1';
            lyr['source-layer'] = getScaleObject(dataRef.current.scale)?.['source-layer'];
            return lyr;
        });
    };

    const updateChloroplethStyleLayer = (styleLayerName, reducer = (lyr) => lyr) => {
        updateVectorStyleLayer(chloroplethLayer.current, styleLayerName, reducer);
    };
    const updatehoveredStyleLayer = (styleLayerName, reducer = (lyr) => lyr) => {
        // updateVectorStyleLayer(hoverLayer.current, styleLayerName, reducer);
    };
    const updateSelectedStyleLayer = (styleLayerName, reducer = (lyr) => lyr) => {
        updateVectorStyleLayer(hoverLayer.current, styleLayerName, reducer);
    };
    const updateVectorStyleLayer = (vectorLayer, styleLayerName, reducer = (lyr) => lyr) => {
        if (!vectorLayer || !'getStyleLayer' in vectorLayer) return;
        try {
            const styleLayer = vectorLayer.getStyleLayer(styleLayerName);
            if (!styleLayer) return;
            vectorLayer.setStyleLayer(reducer(styleLayer));
        } catch (err) {
            // console.log('***=========***');
            // console.log(err);
        }
    };
    useEffect(() => {
        if (selectedFeature.GEOID + '' !== selectedFeatureGEOID) {
            selectedFeatureGEOID.current = selectedFeature.GEOID;
            styleSelectedFeature(selectedFeatureGEOID.current);
        }
    }, [selectedFeature, mapReadOnlyZoom]);

    useEffect(() => {
        if (!mapDiv.current && !initMap) return;
        setInitMap(true);
    }, [mapDiv.current]);

    useEffect(() => {
        if (!view.current) return;
        if (!searchResult.address) {
            // view.current.graphics.removeAll();
        } else {
            moveMapToExtent(searchResult.extent, searchResult?.wkid || 4326);
            // addPinToMap(x, y);
        }
        setSearchResult(false);
    }, [searchResult.address]);

    //When data changes update the reference. Precreated functions don't look at most recent value in useState variables
    useEffect(() => {
        dataRef.current = {
            scale: activeScale,
            view: activeViewType,
            timePeriod: activeTimePeriod,
            deathType: activeDeathType,
            measurment: activeMeasurement,
            mapAttribute: activeMapAttribute
        };
    }, [activeViewType, activeScale, activeDeathType, activeTimePeriod, activeMeasurement, activeMapAttribute]);

    useEffect(() => {
        showRelevantScaleStyleLayers();
    }, [activeViewType]);

    useEffect(() => {
        if (!view.current) return;
        reLoadChloropleth();
        reLoadInfoLayer();
    }, [activeTimePeriod]);

    useEffect(() => {
        if (!view.current || chloroplethLoading.current) return;
        // resetInteractiveFeatures();
        rescaleSelectedFeature();
        // rescaleStyleStepLayers();
        reLoadChloropleth();
    }, [activeScale]);

    useEffect(() => {
        if (!view.current || chloroplethLoading.current) return;
        reColorStyleStepLayers();
    }, [activeMapAttribute]);

    const reLoadInfoLayer = () => {
        if (!infoLayer.current) return;
        const newJson = generateInfoStyleJson({ timePeriod: activeTimePeriod });
        infoLayer.current.loadStyle(newJson);
    };
    const reLoadHoverLayer = () => {
        if (!hoverLayer.current) return;
        const newJson = generateHoverStyleJson({ timePeriod: activeTimePeriod });
        hoverLayer.current.loadStyle(newJson);
    };

    useEffect(() => {
        if (!initMap) return;
        if (map.current !== null) return;
        const mapBaseLayer = new VectorTileLayer({
            url: DATA_URL + '/style/style-basemap-bg.json'
            // url: DATA_URL + '/style/style-basemap-1.json'
        });

        // Create a Basemap with the VectorTileLayer
        const customBasemap = new Basemap({
            baseLayers: [mapBaseLayer],
            title: 'Terrain'
        });

        map.current = new ARCMap({ basemap: customBasemap });

        //US extent
        const extent = new Extent({
            xmin: -14150703.25072198,
            ymin: 1724460.655959669,
            xmax: -7075872.59319234,
            ymax: 6815350.164313462,
            spatialReference: new SpatialReference({ wkid: 3857 })
        });

        //Lilburn extent
        // const extent = new Extent({
        //     xmin: -84.222446,
        //     ymin: 33.805915,
        //     xmax: -84.046446,
        //     ymax: 33.981915,
        //     spatialReference: new SpatialReference({ wkid: 4326 })
        // });

        view.current = new MapView({
            container: mapDiv.current,
            map: map.current,
            extent: extent,
            constraints: {
                minZoom: 13,
                maxZoom: 3,
                rotationEnabled: false
            }
        });
        view.current.ui.move(['zoom'], 'top-right');
        const styleJson = generateChloroplethStyleJson({
            timePeriod: activeTimePeriod,
            sourceLayer: getScaleObject(dataRef.current.scale)?.['source-layer'],
            view: activeViewType,
            mapAttribute: activeMapAttribute,
            scale: dataRef.current.scale
        });
        chloroplethLayer.current = new VectorTileLayer({
            id: 'chloropleth-layer',
            blendMode: 'multiply',
            style: styleJson
        });

        const hoverJson = generateHoverStyleJson({ timePeriod: activeTimePeriod });
        hoverLayer.current = new VectorTileLayer({
            id: 'hover-layer',
            style: hoverJson
        });
        const infoJson = generateInfoStyleJson({ timePeriod: activeTimePeriod });
        infoLayer.current = new VectorTileLayer({
            id: 'hit-layer',
            style: infoJson
        });
        const mapLabelsLayer = new VectorTileLayer({
            url: DATA_URL + '/style/style-basemap-label.json'
        });
        reactiveUtils.watch(
            () => [view.current.stationary, view.current.zoom, view.current.updating],
            ([stationary, zoom, updating]) => {
                if (stationary && !updating) {
                    if (zoomRef.current !== zoom) {
                        zoomRef.current = zoom;
                        setMapReadOnlyZoom(zoom);
                    }
                }
            }
        );

        view.current
            .when()
            .then(() => {
                return infoLayer.current.when();
            })
            .then((layer) => {
                return view.current.whenLayerView(layer);
            })

            .then((layerView) => {
                view.current.on('pointer-move', handleFeatureHover);
                view.current.on('immediate-click', handleFeatureClick);
                view.current.on('pointer-leave', clearHoveredFeature);
                // view.current.on('layerview-create', reColorStyleStepLayers);
            });
        map.current.add(infoLayer.current);
        map.current.add(chloroplethLayer.current);
        map.current.add(hoverLayer.current);
        map.current.add(mapLabelsLayer);
    }, [initMap]);

    return (
        <>
            <div id="map-view-container" ref={mapDiv} style={{ height: '500px', windth: '500px' }} aria-label="Interactive Map"></div>
            <FullExtentWidget view={view} />
            <AlaskaWidget view={view} />
            <HawaiiWidget view={view} />
            {/* <RemoveMarkerWidget view={view} /> */}
        </>
    );
};
export default Map;
